<template>
  <article
    class="toast-item"
    :class="[
      `toast-item--${type}`,
      { 'toast-item--entering': entering, 'toast-item--leaving': leaving },
    ]"
    role="alert"
  >
    <div class="toast-item__accent" aria-hidden="true" />

    <div class="toast-item__icon-wrap" aria-hidden="true">
      <component :is="iconComponent" class="toast-item__icon" />
    </div>

    <div class="toast-item__body">
      <p class="toast-item__title">{{ title }}</p>
      <p class="toast-item__message">{{ message }}</p>
    </div>

    <button
      type="button"
      class="toast-item__close"
      aria-label="Cerrar notificación"
      @click="handleClose"
    >
      <XMarkIcon class="h-4 w-4" />
    </button>

    <div
      class="toast-item__progress"
      :style="{ width: progress + '%', transitionDuration: duration + 'ms' }"
    />
  </article>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/solid';
import type { NotificationType } from '@/store/notifications';

interface Props {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  duration: number;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close', id: number): void;
}>();

const entering = ref(true);
const leaving = ref(false);
const progress = ref(100);
let progressInterval: ReturnType<typeof setInterval> | null = null;

const iconComponent = computed(() => {
  switch (props.type) {
    case 'success':
      return CheckCircleIcon;
    case 'error':
      return XCircleIcon;
    case 'warning':
      return ExclamationTriangleIcon;
    default:
      return InformationCircleIcon;
  }
});

onMounted(() => {
  setTimeout(() => {
    entering.value = false;
  }, 50);

  const step = 100 / (props.duration / 16);
  progressInterval = setInterval(() => {
    progress.value -= step;
    if (progress.value <= 0) {
      progress.value = 0;
      if (progressInterval) clearInterval(progressInterval);
    }
  }, 16);
});

onUnmounted(() => {
  if (progressInterval) clearInterval(progressInterval);
});

function handleClose() {
  leaving.value = true;
  setTimeout(() => {
    emit('close', props.id);
  }, 280);
}
</script>

<style scoped>
.toast-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: min(100vw - 2rem, 22rem);
  max-width: 26rem;
  padding: 0.9rem 0.85rem 0.95rem 0;
  border-radius: 0.85rem;
  border: 1px solid var(--toast-border, rgba(255, 255, 255, 0.12));
  background: var(--toast-bg, #141c22);
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.38),
    0 4px 12px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.28s ease;
}

.toast-item--entering {
  opacity: 0;
  transform: translateX(1.25rem);
}

.toast-item--leaving {
  opacity: 0;
  transform: translateX(1.25rem);
}

.toast-item__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--toast-accent, #2dd4bf);
  border-radius: 0.85rem 0 0 0.85rem;
}

.toast-item__icon-wrap {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  margin-left: 0.85rem;
  border-radius: 0.65rem;
  background: var(--toast-icon-bg, rgba(45, 212, 191, 0.18));
  color: var(--toast-icon-color, #5eead4);
}

.toast-item__icon {
  width: 1.25rem;
  height: 1.25rem;
}

.toast-item__body {
  flex: 1;
  min-width: 0;
  padding-top: 0.1rem;
}

.toast-item__title {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.01em;
  color: var(--toast-title, #f8fafc);
}

.toast-item__message {
  margin-top: 0.3rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  color: var(--toast-message, #cbd5e1);
}

.toast-item__close {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 1.85rem;
  height: 1.85rem;
  margin-right: 0.35rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--toast-close, #94a3b8);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.toast-item__close:hover {
  background: var(--toast-close-hover-bg, rgba(255, 255, 255, 0.08));
  color: var(--toast-title, #f8fafc);
}

.toast-item__progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background: var(--toast-accent, #2dd4bf);
  border-radius: 0 0 0 0.85rem;
  transition-property: width;
  transition-timing-function: linear;
}

/* —— Types (dark) —— */
.toast-item--success {
  --toast-bg: #0f1c19;
  --toast-border: rgba(45, 212, 191, 0.35);
  --toast-accent: #14b8a6;
  --toast-icon-bg: rgba(20, 184, 166, 0.22);
  --toast-icon-color: #5eead4;
  --toast-title: #ecfdf5;
  --toast-message: #bae6d8;
}

.toast-item--error {
  --toast-bg: #1c1214;
  --toast-border: rgba(248, 113, 113, 0.38);
  --toast-accent: #ef4444;
  --toast-icon-bg: rgba(239, 68, 68, 0.22);
  --toast-icon-color: #fca5a5;
  --toast-title: #fef2f2;
  --toast-message: #fecaca;
}

.toast-item--warning {
  --toast-bg: #1c1810;
  --toast-border: rgba(251, 191, 36, 0.38);
  --toast-accent: #f59e0b;
  --toast-icon-bg: rgba(245, 158, 11, 0.22);
  --toast-icon-color: #fcd34d;
  --toast-title: #fffbeb;
  --toast-message: #fde68a;
}

.toast-item--info {
  --toast-bg: #101820;
  --toast-border: rgba(96, 165, 250, 0.35);
  --toast-accent: #3b82f6;
  --toast-icon-bg: rgba(59, 130, 246, 0.22);
  --toast-icon-color: #93c5fd;
  --toast-title: #eff6ff;
  --toast-message: #bfdbfe;
}

/* —— Light theme —— */
:global(html.theme-light) .toast-item--success {
  --toast-bg: #ecfdf5;
  --toast-border: rgba(13, 148, 136, 0.35);
  --toast-icon-bg: rgba(13, 148, 136, 0.14);
  --toast-icon-color: #0f766e;
  --toast-title: #134e4a;
  --toast-message: #334155;
  --toast-close: #64748b;
  --toast-close-hover-bg: rgba(13, 148, 136, 0.1);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.08);
}

:global(html.theme-light) .toast-item--error {
  --toast-bg: #fef2f2;
  --toast-border: rgba(220, 38, 38, 0.3);
  --toast-icon-bg: rgba(220, 38, 38, 0.12);
  --toast-icon-color: #b91c1c;
  --toast-title: #7f1d1d;
  --toast-message: #44403c;
  --toast-close: #64748b;
  --toast-close-hover-bg: rgba(220, 38, 38, 0.08);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.08);
}

:global(html.theme-light) .toast-item--warning {
  --toast-bg: #fffbeb;
  --toast-border: rgba(217, 119, 6, 0.32);
  --toast-icon-bg: rgba(217, 119, 6, 0.14);
  --toast-icon-color: #b45309;
  --toast-title: #78350f;
  --toast-message: #44403c;
  --toast-close: #64748b;
  --toast-close-hover-bg: rgba(217, 119, 6, 0.1);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.08);
}

:global(html.theme-light) .toast-item--info {
  --toast-bg: #eff6ff;
  --toast-border: rgba(37, 99, 235, 0.3);
  --toast-icon-bg: rgba(37, 99, 235, 0.12);
  --toast-icon-color: #1d4ed8;
  --toast-title: #1e3a8a;
  --toast-message: #334155;
  --toast-close: #64748b;
  --toast-close-hover-bg: rgba(37, 99, 235, 0.08);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.12),
    0 2px 8px rgba(15, 23, 42, 0.08);
}
</style>
